import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { JobStatus } from "../../../../pages/playground/types";

export interface UseJobListFiltersReturn {
  statusFilter: JobStatus | null;
  columnFilter: string | null;
  handleStatusFilterChange: (value: JobStatus | null) => void;
  handleColumnFilterChange: (value: string | null) => void;
}

/**
 * Hook to manage job list filters with URL parameter synchronization
 *
 * Filters are persisted in URL search parameters:
 * - ?status=ready
 * - ?column=garment_type
 *
 * @returns Filter state and change handlers
 */
export function useJobListFilters(): UseJobListFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL parameters
  const [statusFilter, setStatusFilter] = useState<JobStatus | null>(
    searchParams.get("status") as JobStatus | null
  );
  const [columnFilter, setColumnFilter] = useState<string | null>(
    searchParams.get("column")
  );

  const handleColumnFilterChange = useCallback(
    (value: string | null) => {
      setColumnFilter(value);

      // Update URL params
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("column", value);
      } else {
        newParams.delete("column");
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleStatusFilterChange = useCallback(
    (value: JobStatus | null) => {
      setStatusFilter(value);

      // Update URL params
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("status", value);
      } else {
        newParams.delete("status");
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  return {
    statusFilter,
    columnFilter,
    handleStatusFilterChange,
    handleColumnFilterChange,
  };
}
